import {useLinkTo} from '@react-navigation/native';
import {useState} from 'react';
import {ScrollView, StyleSheet} from 'react-native';
import {DataTable, Searchbar, TouchableRipple} from 'react-native-paper';

import {
  LimitedPartner,
  Member,
  Property,
  Subscriber,
  useFetchLimitedPartners,
  useFetchMembers,
  useProperties,
  useFetchSubscribers,
} from '../hooks/useDomainData';
import {Person, usePeople} from '../hooks/usePersonInfo';

const minimumTextSearchString = 3;
const minimumNumberSearchString = 1;

const isNumeric = (num: any) =>
  (typeof num === 'number' || (typeof num === 'string' && num.trim() !== '')) &&
  !isNaN(num as number);

const shouldSearch = (searchString: string) => {
  if (
    searchString.length >= minimumTextSearchString ||
    (isNumeric(searchString) &&
      searchString.length >= minimumNumberSearchString)
  ) {
    return true;
  }
  return false;
};

const searchPeople = (
  search: string,
  people?: ReadonlyArray<Person>,
): Person[] | undefined => {
  if (people === undefined || !shouldSearch(search)) {
    return undefined;
  }

  return people.filter(person => {
    if (
      person.firstName?.toLowerCase().includes(search) ||
      person.lastName?.toLowerCase().includes(search) ||
      person.hubspotId?.toString().toLowerCase().includes(search)
    ) {
      return true;
    }
  });
};

const searchMembers = (
  search: string,
  members?: ReadonlyArray<Member>,
): Member[] | undefined => {
  if (members === undefined || !shouldSearch(search)) {
    return undefined;
  }

  return members.filter(member =>
    member.memberName.toLowerCase().includes(search),
  );
};

const searchLimitedPartners = (
  search: string,
  limitedPartners?: ReadonlyArray<LimitedPartner>,
): LimitedPartner[] | undefined => {
  if (limitedPartners === undefined || !shouldSearch(search)) {
    return undefined;
  }
  return limitedPartners.filter(
    lp =>
      lp.limitedPartnerName.toLowerCase().includes(search) ||
      (lp.taxIdNumber + '').includes(search),
  );
};

const searchSubscribers = (
  search: string,
  subscribers?: ReadonlyArray<Subscriber>,
): Subscriber[] | undefined => {
  if (subscribers === undefined || !shouldSearch(search)) {
    return undefined;
  }
  return subscribers.filter(sub =>
    sub.subscriberName.toLowerCase().includes(search),
  );
};

const searchProperties = (
  search: string,
  properties?: Property[],
): Property[] | undefined => {
  if (properties === undefined || !shouldSearch(search)) {
    return undefined;
  }
  return properties.filter(prop =>
    prop.propertyName.toLowerCase().includes(search),
  );
};

export const Search = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const onChangeSearch = (query: string) => setSearchQuery(query);

  const linkTo = useLinkTo();
  let properties = useProperties();
  const people = usePeople();
  const members = useFetchMembers();
  const limitedPartners = useFetchLimitedPartners();
  const subscribers = useFetchSubscribers();

  const searchQueryLower = searchQuery.toLowerCase();

  const peopleCopy = searchPeople(searchQueryLower, people);
  const membersCopy = searchMembers(searchQueryLower, members);
  const limitedPartnersCopy = searchLimitedPartners(
    searchQueryLower,
    limitedPartners,
  );
  const subscribersCopy = searchSubscribers(searchQueryLower, subscribers);
  properties = searchProperties(searchQueryLower, properties);
  const totalResults =
    (peopleCopy?.length ?? 0) +
    (membersCopy?.length ?? 0) +
    (limitedPartnersCopy?.length ?? 0) +
    (subscribersCopy?.length ?? 0) +
    (properties?.length ?? 0);

  const resultLinkTo = (type: string, id: number) => {
    return linkTo('/investor/' + type + '/' + id);
  };

  return (
    <ScrollView style={[styles.container, styles.content]}>
      <Searchbar
        placeholder="Search"
        onChangeText={onChangeSearch}
        value={searchQuery}
      />
      <DataTable>
        <DataTable.Header>
          <DataTable.Title>Result Type</DataTable.Title>
          <DataTable.Title>Result</DataTable.Title>
        </DataTable.Header>

        {!shouldSearch(searchQuery) && (
          <DataTable.Row>
            <DataTable.Cell>
              No search in effect. Type {minimumTextSearchString} or more
              characters (or {minimumNumberSearchString} or more numbers) above.
            </DataTable.Cell>
          </DataTable.Row>
        )}
        {shouldSearch(searchQuery) && totalResults === 0 && (
          <DataTable.Row>
            <DataTable.Cell style={styles.center}>
              No search results found
            </DataTable.Cell>
          </DataTable.Row>
        )}
        {totalResults > 0 &&
          peopleCopy &&
          peopleCopy.map(person => (
            <TouchableRipple
              key={'person' + person.personId}
              onPress={() => resultLinkTo('person', person.personId)}>
              <DataTable.Row key={'person' + person.personId}>
                <DataTable.Cell>Person</DataTable.Cell>
                <DataTable.Cell>
                  {person.firstName} {person.lastName}
                  {person.hubspotId ? ` (Hubspot: ${person.hubspotId})` : ''}
                </DataTable.Cell>
              </DataTable.Row>
            </TouchableRipple>
          ))}
        {totalResults > 0 &&
          membersCopy &&
          membersCopy.map(member => (
            <TouchableRipple
              key={'member' + member.memberId}
              onPress={() => {
                linkTo('/investor/member/' + member.memberId);
              }}>
              <DataTable.Row key={'member' + member.memberId}>
                <DataTable.Cell>Member</DataTable.Cell>
                <DataTable.Cell>{member.memberName}</DataTable.Cell>
              </DataTable.Row>
            </TouchableRipple>
          ))}
        {totalResults > 0 &&
          limitedPartnersCopy &&
          limitedPartnersCopy.map(lp => (
            <TouchableRipple
              key={'lp' + lp.limitedPartnerId}
              onPress={() => {
                linkTo('/investor/limited-partner/' + lp.limitedPartnerId);
              }}>
              <DataTable.Row key={'lp' + lp.limitedPartnerId}>
                <DataTable.Cell>Limited Partner</DataTable.Cell>
                <DataTable.Cell>
                  {lp.limitedPartnerName} (Tax ID: {lp.taxIdNumber})
                </DataTable.Cell>
              </DataTable.Row>
            </TouchableRipple>
          ))}
        {totalResults > 0 &&
          subscribersCopy &&
          subscribersCopy.map(subscriber => (
            <TouchableRipple
              key={'sub' + subscriber.subscriberId}
              onPress={() => {
                linkTo('/investor/subscriber/' + subscriber.subscriberId);
              }}>
              <DataTable.Row key={'sub' + subscriber.subscriberId}>
                <DataTable.Cell>Subscriber</DataTable.Cell>
                <DataTable.Cell>{subscriber.subscriberName}</DataTable.Cell>
              </DataTable.Row>
            </TouchableRipple>
          ))}
        {totalResults > 0 &&
          properties &&
          properties.map(property => (
            <DataTable.Row key={'prop' + property.propertyName}>
              <DataTable.Cell>Property</DataTable.Cell>
              <DataTable.Cell>{property.propertyName}</DataTable.Cell>
            </DataTable.Row>
          ))}
      </DataTable>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: 'relative',
  },
  content: {
    padding: 10,
  },
  center: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
});
